import React, { Component, Fragment } from "react";

import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Table from "@material-ui/core/Table";

import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";

import styled from "styled-components";

import Pagination from "./components/Pagination";
import _ from "lodash";
import { ASC, DESC } from "../../../resources/constants";
import { TableSortLabel } from "@material-ui/core";

const Container = styled.div`
  width: 100%;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
`;

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  max-height: 600px;
`;

const StyledTable = styled(Table)`
  background: var(--color-surface) !important;
`;

const StyledTableHead = styled(TableHead)`
  background: var(--color-surface-subtle) !important;
`;

const StyledTableCell = styled(TableCell)`
  color: var(--color-text) !important;
  border-bottom: 1px solid var(--color-border-subtle) !important;
  font-family: var(--font-sans) !important;
`;

const HeaderCellText = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary);
`;

const BodyCellText = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text);
  line-height: var(--line-height-normal);
`;

const BodyRow = styled(TableRow)<{ index: number }>`
  transition: background var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface-subtle) !important;
  }
`;

const StyledSortLabel = styled(TableSortLabel)`
  &.MuiTableSortLabel-root {
    color: var(--color-text-secondary) !important;
  }

  &.MuiTableSortLabel-active,
  &.MuiTableSortLabel-root.MuiTableSortLabel-active {
    color: var(--color-text) !important;
  }

  .MuiTableSortLabel-icon {
    color: var(--color-text-secondary) !important;
  }
`;

const ActionButtonsWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;

  .MuiIconButton-root {
    color: var(--color-text-secondary);
    padding: 6px;
    transition: background var(--duration-fast) var(--ease-standard),
      color var(--duration-fast) var(--ease-standard);
  }

  .MuiIconButton-root:hover {
    background: var(--color-surface-subtle);
    color: var(--color-text);
  }

  .MuiIconButton-root[aria-label="Delete"]:hover {
    color: var(--color-danger);
    background: var(--color-danger-subtle);
  }

  .MuiSvgIcon-root {
    font-size: 18px;
  }
`;

type Props = {
  onRemoveIconClicked?: Function;
  onDetailIconClicked?: Function;
  onEditIconClicked?: Function;
  onExtraDialogOpen?: Function;
  setItemsPerPage?: Function;
  updatePageIndex: Function;
  tabConfig: Array<any>;
  dataset: Array<any>;
  hidePagination?: boolean;
  canBeRemoved?: boolean;
  canBeEdited?: boolean;
  itemsPerPage?: number;
  currentPage: number;
};

type State = {
  rowsPerPage: number;
  columnName: string;
  isExtraDialogOpen: boolean;
  contextItem: any;
  sortedBy: string;
  sortDirection: any;
  data: Array<any>;
};

type SortOrder = "asc" | "desc";

class CustomTable extends Component<Props, State> {
  state = {
    rowsPerPage: 10,
    columnName: null,
    sortedBy: "",
    sortDirection: "asc",
    data: this.props.dataset,
    isExtraDialogOpen: false,
    contextItem: null,
  };

  componentDidMount() {
    const { itemsPerPage, dataset } = this.props;
    this.setState({ ...this.state, data: dataset });
    this.handleRowsPerPageValue(itemsPerPage);
  }

  componentWillReceiveProps(nextProps) {
    const { itemsPerPage } = nextProps;
    this.handleRowsPerPageValue(itemsPerPage);
  }

  onChangeRowsPerPage = (rowsPerPage: number): void => {
    const { setItemsPerPage } = this.props;

    this.setState(
      { rowsPerPage },
      () => !!setItemsPerPage && setItemsPerPage(rowsPerPage)
    );
  };

  handleRowsPerPageValue = (itemsPerPage: any): void => {
    const { rowsPerPage } = this.state;
    this.setState({ rowsPerPage: itemsPerPage || rowsPerPage });
  };

  sortTable = (columnName) => {
    let sortDirection = "asc";
    if (this.state.columnName === columnName) {
      if (this.state.sortDirection === "asc") {
        sortDirection = "desc";
      }
    }
    let sortValue;
    switch (sortDirection) {
      case "asc":
        sortValue = 1;
        break;
      case "desc":
        sortValue = -1;
        break;
      default:
        sortValue = 0;
        break;
    }

    const dataSorted = this.props.dataset.sort((x1, x2) =>
      x1[columnName] < x2[columnName] ? -1 * sortValue : sortValue
    );

    this.setState({ columnName, sortDirection, data: dataSorted });
  };

  setData = (data) => {
    this.setState({ data });
  };

  getComparator<Key extends keyof any>(
    order: SortOrder,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === "desc"
      ? (a, b) => this.descendingComparator(a, b, orderBy)
      : (a, b) => -this.descendingComparator(a, b, orderBy);
  }

  descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  }

  renderActionsSection = (item: any, index: number): React.ReactNode => {
    const {
      onDetailIconClicked,
      onRemoveIconClicked,
      onEditIconClicked,
      canBeRemoved,
      canBeEdited,
    } = this.props;

    const { canEditSingleItem } = item;
    const { rowsPerPage } = this.state;

    return (
      <ActionButtonsWrapper>
        {(canBeEdited || canEditSingleItem) && (
          <IconButton
            onClick={() => onEditIconClicked({ ...item, index })}
            aria-label="Edit"
            disableRipple
          >
            <EditIcon />
          </IconButton>
        )}
        {onDetailIconClicked && (
          <IconButton
            onClick={() => onDetailIconClicked(item, rowsPerPage)}
            aria-label="Search"
            disableRipple
          >
            <SearchIcon />
          </IconButton>
        )}
        {canBeRemoved && (
          <IconButton
            onClick={() => onRemoveIconClicked(item, rowsPerPage)}
            aria-label="Delete"
            disableRipple
          >
            <DeleteIcon />
          </IconButton>
        )}
      </ActionButtonsWrapper>
    );
  };

  renderHeader = (columnsTitles: Array<string>) => (
    <StyledTableHead>
      <TableRow>
        {columnsTitles.map((title, index) => (
          <StyledTableCell
            key={title + index}
            //@ts-ignore
            sortDirection={
              this.state.columnName === title ? this.state.sortDirection : false
            }
          >
            <StyledSortLabel
              active={this.state.columnName === title}
              //@ts-ignore
              direction={
                this.state.columnName === title
                  ? this.state.sortDirection
                  : ASC
              }
              onClick={() => this.sortTable(title)}
            >
              <HeaderCellText>{title}</HeaderCellText>
            </StyledSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </StyledTableHead>
  );

  renderExtraComponent = (Component, row) => {
    let { onExtraDialogOpen } = this.props;
    return (
      <Component
        onDialogOpen={onExtraDialogOpen}
        values={row}
        setData={this.setData}
        data={this.state.data}
      />
    );
  };

  renderRowCell = (
    row: any,
    dataFields: Array<string>,
    index: number,
    tabConfig: any
  ) => {
    const rowData = [...dataFields, "actions"];

    return (
      <Fragment>
        {rowData.map((key: any, idx) => {
          return (
            <StyledTableCell key={idx} variant="body" padding="default">
              <>
                {key === "actions" ? (
                  this.renderActionsSection(row, idx)
                ) : key[0] === "component" ? (
                  this.renderExtraComponent(tabConfig[idx].component, row)
                ) : (
                  <BodyCellText>
                    {key.map((value: any) => {
                      let result = _.get(row, value);
                      if (!!tabConfig[idx].transform) {
                        result = tabConfig[idx].transform(result);
                      }
                      if (result) return `${result} `;
                    })}
                  </BodyCellText>
                )}
              </>
            </StyledTableCell>
          );
        })}
      </Fragment>
    );
  };

  renderRows = (
    rows: Array<any>,
    dataFields: Array<string>,
    tabConfig: any
  ): React.ReactNode => (
    <TableBody>
      {rows.map((row, index) => {
        if (!row) return null;
        return (
          <BodyRow index={index} key={row.id}>
            {this.renderRowCell(row, dataFields, index, tabConfig)}
          </BodyRow>
        );
      })}
    </TableBody>
  );

  render() {
    const { updatePageIndex, currentPage, tabConfig, dataset, hidePagination } =
      this.props;
    const { rowsPerPage } = this.state;

    const columnsTitles = tabConfig.map((item) => item.columnTitle);
    const dataFields = tabConfig.map((item) => item.dataField);

    const paginationOffset = currentPage * rowsPerPage + rowsPerPage;
    const paginationStartIndex = currentPage * rowsPerPage;

    const datasetWithPagination = dataset.slice(
      paginationStartIndex,
      paginationOffset
    );

    return (
      <Container>
        <TableScroll>
          <StyledTable>
            <>
              {this.renderHeader([...columnsTitles, ""])}
              {this.renderRows(datasetWithPagination, dataFields, tabConfig)}
            </>
          </StyledTable>
        </TableScroll>
        {!hidePagination && (
          <Pagination
            onChangeRowsPerPage={this.onChangeRowsPerPage}
            datasetLength={dataset.length}
            onPageChange={updatePageIndex}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
          />
        )}
      </Container>
    );
  }
}

export default CustomTable;
